import type {
  ColumnFilters,
  ColumnFilterValue,
  FlowFilter,
  FlowFilterCondition,
} from '@/models/flowFilter.type';
import dayjs from 'dayjs';

/**
 * Convertit la valeur d'un filtre de colonne en conditions. Une valeur vide donne une liste vide.
 * Les bornes d'une plage restent des conditions distinctes (combinées en `AND` par l'appelant).
 */
const toConditions = (value: ColumnFilterValue): FlowFilterCondition[] => {
  switch (value.kind) {
    case 'text': {
      const text = value.value.trim();
      return text ? [{ _icontains: text }] : [];
    }
    case 'select':
      return value.values.length > 0 ? [{ _in: value.values }] : [];
    case 'number-range': {
      const { min, max } = value;
      if (min != null && max != null) return [{ _between: [min, max] }];
      if (min != null) return [{ _gte: min }];
      if (max != null) return [{ _lte: max }];
      return [];
    }
    case 'date-range': {
      const conditions: FlowFilterCondition[] = [];
      if (value.from) {
        conditions.push({ _gte: dayjs(value.from).startOf('day').toISOString() });
      }
      if (value.to) {
        conditions.push({ _lt: dayjs(value.to).add(1, 'day').startOf('day').toISOString() });
      }
      return conditions;
    }
    case 'boolean':
      return [{ _eq: value.value }];
  }
};

/**
 * Colonnes de relation filtrées via une colonne texte listant les identifiants liés, encadrés de
 * virgules (ex. `{ sus: 'su_ids' }` : une ingestion des services 3 et 12 a `su_ids = ',3,12,'`).
 * Un filtre `select` devient `{ su_ids: { _contains: ',12,' } }` (les virgules évitent que 12
 * corresponde aussi à 112), combiné en `_or` pour plusieurs identifiants.
 */
export type RelationColumns = Record<string, string>;

const toRelationClause = (
  idsColumn: string,
  condition: FlowFilterCondition,
): FlowFilter | undefined => {
  const values = condition._in;
  const ids = (Array.isArray(values) ? values : [])
    .map(Number)
    .filter(Number.isInteger)
    .map(id => ({ [idsColumn]: { _contains: `,${id},` } }));
  if (ids.length === 0) return undefined;
  return ids.length === 1 ? ids[0] : { _or: ids };
};

/**
 * Construit le paramètre `filter` d'un flow à partir des filtres de colonnes.
 * Retourne `undefined` si aucun filtre n'est actif (le paramètre n'est alors pas envoyé).
 */
export const buildFlowFilter = (
  filters: ColumnFilters,
  relationColumns: RelationColumns = {},
): FlowFilter | undefined => {
  const clauses: FlowFilter[] = Object.entries(filters).flatMap(([column, value]) =>
    toConditions(value).flatMap((condition): FlowFilter[] => {
      if (!(column in relationColumns)) return [{ [column]: condition }];
      const clause = toRelationClause(relationColumns[column], condition);
      return clause ? [clause] : [];
    }),
  );

  return clauses.length > 0 ? { _and: clauses } : undefined;
};
